import React, { useCallback, useMemo } from 'react';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import { useMediaQuery, MOBILE_BREAKPOINT } from '../../hooks/useMediaQuery';
import { paymentStatusColors } from '../../styles/colors';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import Card, { CardHeader, CardTitle, CardContent, CardFooter } from '../ui/Card';
import { TrendingUp } from 'lucide-react';

export interface PaymentRevenueData {
  month: string;      // X-axis label (month, week, or year)
  paid: number;       // Paid - green
  overdue: number;    // Overdue - red
  pending: number;    // Pending - orange
  total: number;      // Sum for overall rounding
  isCurrentMonth?: boolean; // Kept for backward compatibility (current month)
  monthType?: 'past' | 'current' | 'future'; // Temporal position for styling
  dotY?: number;      // Y position for dots
}

interface PaymentRevenueChartProps {
  data: PaymentRevenueData[];
  title?: string;
  showTitle?: boolean;
  className?: string;
  /** Explicit chart height in px. Default: compact on phone, taller on desktop. */
  chartHeight?: number;
  /** Stretch chart area to fill parent card height (e.g. Finances side-by-side layout). */
  fillHeight?: boolean;
}

const CHART_HEIGHT_MOBILE = 220;
const CHART_HEIGHT_DESKTOP = 384;

const PaymentRevenueChart: React.FC<PaymentRevenueChartProps> = ({
  data,
  title,
  showTitle = true,
  className = '',
  chartHeight,
  fillHeight = false,
}) => {
  const { t } = useI18n();
  const { formatCurrency } = useTranslations();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const resolvedHeight = chartHeight ?? (isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT_DESKTOP);
  const containerHeight = fillHeight && !isMobile ? '100%' : resolvedHeight;

  const paidColor = paymentStatusColors.paid.hex;
  const sentColor = paymentStatusColors.sent.hex;
  const overdueColor = paymentStatusColors.overdue.hex;

  const formatCurrencyTick = useCallback(
    (value: any) =>
      formatCurrency(typeof value === 'number' ? value : 0, 'EUR', { maximumFractionDigits: 0 }),
    [formatCurrency]
  );

  const chartData = data;

  const grandTotal = useMemo(
    () =>
      chartData.reduce(
        (sum, d) => sum + (d.paid || 0) + (d.pending || 0) + (d.overdue || 0),
        0
      ),
    [chartData]
  );

  const labelTypeMap = useMemo(() => {
    const map: Record<string, 'past' | 'current' | 'future' | undefined> = {};
    chartData.forEach((d) => {
      if (d.monthType) map[d.month] = d.monthType;
    });
    return map;
  }, [chartData]);

  const renderTick = useCallback(
    ({ x, y, payload }: any) => {
      const type = labelTypeMap[payload.value] || 'past';
      const color = type === 'current' ? '#1e40af' : type === 'future' ? '#9ca3af' : '#374151';
      const fontWeight = type === 'current' ? 'bold' : 'normal';
      const fontSize = type === 'current' ? 12 : 11;
      return (
        <g transform={`translate(${x},${y})`}>
          <text
            x={0}
            y={0}
            dy={14}
            textAnchor="middle"
            fill={color}
            fontSize={fontSize}
            fontWeight={fontWeight}
          >
            {payload.value}
          </text>
        </g>
      );
    },
    [labelTypeMap]
  );

  const renderStackTotalLabel = useCallback(
    (props: any) => {
      const { x, y, width, index } = props;
      const total = chartData[index]?.total ?? 0;
      if (!total || width == null || x == null || y == null) return null;

      return (
        <text
          x={x + width / 2}
          y={y - 6}
          textAnchor="middle"
          fill="#374151"
          fontSize={isMobile ? 9 : 11}
          fontWeight={600}
          className="pointer-events-none"
        >
          {formatCurrency(total, 'EUR', { maximumFractionDigits: 0 })}
        </text>
      );
    },
    [chartData, formatCurrency, isMobile]
  );

  const CustomTooltip = useCallback(
    ({ active, payload, label }: any) => {
      if (active && payload && payload.length) {
        const total = payload.reduce((sum: number, entry: any) => sum + entry.value, 0);
        const dataPoint = data.find((d) => d.month === label);
        const isCurrent = Boolean(
          dataPoint && (dataPoint.monthType === 'current' || dataPoint.isCurrentMonth)
        );

        const getLabelMode = (text: string): 'week' | 'month' | 'year' => {
          if (/^S\d+\s\d{4}$/.test(text)) return 'week';
          if (/^\d{4}$/.test(text)) return 'year';
          return 'month';
        };
        const modeForTooltip = getLabelMode(label);

        const getTooltipColor = (dataKey: string) => {
          switch (dataKey) {
            case 'paid':
              return paidColor;
            case 'pending':
              return sentColor;
            case 'overdue':
              return overdueColor;
            default:
              return '#6b7280';
          }
        };

        const getTooltipLabel = (dataKey: string) => {
          switch (dataKey) {
            case 'paid':
              return t('payments.statusLabels.paid');
            case 'pending':
              return t('payments.statusLabels.pending');
            case 'overdue':
              return t('payments.statusLabels.overdue');
            default:
              return dataKey;
          }
        };

        return (
          <div className="bg-white/95 backdrop-blur-md border border-gray-200/80 rounded-xl shadow-lg shadow-black/10 p-4">
            <div className="flex items-center gap-2 mb-3">
              <p className="font-semibold text-gray-900">{label}</p>
              {isCurrent && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  {modeForTooltip === 'week'
                    ? t('finances.currentWeek')
                    : modeForTooltip === 'year'
                      ? t('finances.currentYear')
                      : t('finances.currentMonth')}
                </span>
              )}
            </div>
            <div className="space-y-2">
              {payload.map((entry: any, index: number) => (
                <div key={index} className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: getTooltipColor(entry.dataKey) }}
                    />
                    <span className="text-sm text-gray-600">{getTooltipLabel(entry.dataKey)}:</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(entry.value)}
                  </span>
                </div>
              ))}
              <div className="border-t border-gray-200/40 pt-2 mt-2">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-semibold text-gray-900">Total:</span>
                  <span className="text-sm font-bold text-gray-900">{formatCurrency(total)}</span>
                </div>
              </div>
            </div>
          </div>
        );
      }
      return null;
    },
    [data, formatCurrency, overdueColor, paidColor, sentColor, t]
  );

  return (
    <Card className={`${fillHeight ? 'h-full min-h-0' : ''} ${className}`.trim()}>
      {showTitle && (
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-base">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span>{title || t('finances.revenueChart')}</span>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className="flex min-h-0 flex-1 flex-col">
        <div
          className={`flex shrink-0 justify-center gap-4 sm:gap-6 ${isMobile ? 'mb-3' : 'mb-5'}`}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm shadow-sm"
              style={{
                background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                border: `1px solid ${paymentStatusColors.paid.stroke}`,
              }}
            />
            <span className="text-sm text-gray-600">{t('payments.statusLabels.paid')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm shadow-sm"
              style={{
                background: 'linear-gradient(135deg, #fde047 0%, #eab308 100%)',
                border: `1px solid ${paymentStatusColors.sent.stroke}`,
              }}
            />
            <span className="text-sm text-gray-600">{t('payments.statusLabels.pending')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm shadow-sm"
              style={{
                background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)',
                border: `1px solid ${paymentStatusColors.overdue.stroke}`,
              }}
            />
            <span className="text-sm text-gray-600">{t('payments.statusLabels.overdue')}</span>
          </div>
        </div>

        <div
          className={`w-full select-none outline-none focus:outline-none ${
            fillHeight ? 'min-h-0 flex-1' : 'flex-1 min-h-0'
          }`}
          tabIndex={-1}
          style={{
            userSelect: 'none',
            WebkitTapHighlightColor: 'transparent' as any,
            minHeight: isMobile ? `${CHART_HEIGHT_MOBILE}px` : fillHeight ? 240 : '240px',
          }}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          <ResponsiveContainer width="100%" height={containerHeight}>
            <BarChart
              data={chartData}
              margin={
                isMobile
                  ? { top: 18, right: 4, left: 0, bottom: 0 }
                  : { top: 24, right: 8, left: 0, bottom: 0 }
              }
            >
              <defs>
                <linearGradient id="paidGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#4ade80" stopOpacity={1} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0.8} />
                </linearGradient>
                <linearGradient id="sentGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#fde047" stopOpacity={1} />
                  <stop offset="100%" stopColor="#eab308" stopOpacity={0.8} />
                </linearGradient>
                <linearGradient id="overdueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#f87171" stopOpacity={1} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0.8} />
                </linearGradient>

                <pattern id="paidPattern" patternUnits="userSpaceOnUse" width="6" height="6">
                  <rect width="6" height="6" fill="url(#paidGradient)" />
                  <path d="M0,6 L6,0" stroke="#ffffff" strokeWidth="1" opacity="0.8" />
                </pattern>
                <pattern id="sentPattern" patternUnits="userSpaceOnUse" width="6" height="6">
                  <rect width="6" height="6" fill="url(#sentGradient)" />
                  <path d="M0,6 L6,0" stroke="#ffffff" strokeWidth="1" opacity="0.8" />
                </pattern>
                <pattern id="overduePattern" patternUnits="userSpaceOnUse" width="6" height="6">
                  <rect width="6" height="6" fill="url(#overdueGradient)" />
                  <path d="M0,6 L6,0" stroke="#ffffff" strokeWidth="1" opacity="0.8" />
                </pattern>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.4} />
              <XAxis
                dataKey="month"
                stroke="#6b7280"
                tick={renderTick}
                tickLine={false}
                axisLine={true}
                height={isMobile ? 36 : 48}
                hide={false}
              />
              <YAxis
                stroke="#6b7280"
                fontSize={isMobile ? 10 : 12}
                width={isMobile ? 44 : 60}
                tickFormatter={formatCurrencyTick}
                tick={{ fill: '#6b7280' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="paid"
                name="paid"
                stackId="revenue"
                stroke={paymentStatusColors.paid.stroke}
                strokeWidth={1.5}
                isAnimationActive={false}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`paid-${index}`}
                    fill={
                      entry.monthType === 'future' || entry.monthType === 'current'
                        ? 'url(#paidPattern)'
                        : 'url(#paidGradient)'
                    }
                  />
                ))}
              </Bar>
              <Bar
                dataKey="overdue"
                name="overdue"
                stackId="revenue"
                stroke={paymentStatusColors.overdue.stroke}
                strokeWidth={1.5}
                isAnimationActive={false}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`overdue-${index}`}
                    fill={
                      entry.monthType === 'future' || entry.monthType === 'current'
                        ? 'url(#overduePattern)'
                        : 'url(#overdueGradient)'
                    }
                  />
                ))}
              </Bar>
              <Bar
                dataKey="pending"
                name="pending"
                stackId="revenue"
                stroke={paymentStatusColors.sent.stroke}
                strokeWidth={1.5}
                isAnimationActive={false}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`pending-${index}`}
                    fill={
                      entry.monthType === 'future' || entry.monthType === 'current'
                        ? 'url(#sentPattern)'
                        : 'url(#sentGradient)'
                    }
                  />
                ))}
                <LabelList dataKey="total" content={renderStackTotalLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
      <CardFooter>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">
            {t('common.total')} {t('finances.revenue').toLowerCase()}
          </span>
          <span className="font-medium text-gray-900">{formatCurrency(grandTotal)}</span>
        </div>
      </CardFooter>
    </Card>
  );
};

export default React.memo(PaymentRevenueChart);
